import { useCallback, useEffect, useRef, useState } from "react";
import ProductCard from "./ProductCard";
import "./CarruselRelacionados.css";

/**
 * Carrusel de productos relacionados, con avance automatico.
 *
 * Al final de la lista va una tarjeta para ver el catalogo completo de la
 * misma categoria, asi el que llego buscando una pieza puede seguir mirando
 * las de su tipo sin volver atras.
 */

// Cada cuanto avanza. Un segundo es lo que se pidio; es poco tiempo para
// leer una tarjeta, y esta aca arriba para poder subirlo en un solo lugar.
const MS_POR_PASO = 1000;

function CarruselRelacionados({ productos = [], onSelect, onVerTodos }) {
  const pista = useRef(null);
  const [pausado, setPausado] = useState(false);

  // Cuantas tarjetas entran a la vista: de eso depende cuando se llego al
  // final. Se mide en vez de suponerlo, porque el ancho cambia con la
  // ventana.
  const [porVista, setPorVista] = useState(1);
  const [indice, setIndice] = useState(0);

  // La tarjeta de "ver todos" cuenta como una mas para el recorrido.
  const total = productos.length + (onVerTodos ? 1 : 0);
  const ultimo = Math.max(0, total - porVista);

  const medir = useCallback(() => {
    const el = pista.current;
    if (!el) return;

    const primera = el.querySelector(".crel-slot");
    if (!primera) return;

    const anchoSlot = primera.getBoundingClientRect().width;
    if (anchoSlot <= 0) return;

    setPorVista(Math.max(1, Math.round(el.clientWidth / anchoSlot)));
  }, []);

  useEffect(() => {
    medir();

    const el = pista.current;
    if (!el) return;

    const observador = new ResizeObserver(medir);
    observador.observe(el);
    return () => observador.disconnect();
  }, [medir, total]);

  // Si la ventana crece y ahora entran mas tarjetas, el indice puede quedar
  // pasado del final.
  useEffect(() => {
    setIndice((i) => Math.min(i, ultimo));
  }, [ultimo]);

  useEffect(() => {
    if (pausado || total <= porVista) return;

    // A quien le molesta el movimiento no se le mueve.
    const quieto = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (quieto?.matches) return;

    const t = setInterval(() => {
      // Al llegar al final vuelve al principio.
      setIndice((i) => (i >= ultimo ? 0 : i + 1));
    }, MS_POR_PASO);

    return () => clearInterval(t);
  }, [pausado, total, porVista, ultimo]);

  // Quieto mientras la pestana no se ve: mover algo que nadie mira solo
  // gasta bateria.
  useEffect(() => {
    const alCambiar = () => setPausado(document.hidden);
    document.addEventListener("visibilitychange", alCambiar);
    return () => document.removeEventListener("visibilitychange", alCambiar);
  }, []);

  if (productos.length === 0) return null;

  const irA = (i) => setIndice(Math.min(Math.max(i, 0), ultimo));

  return (
    <div
      className="crel"
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      onFocus={() => setPausado(true)}
      onBlur={() => setPausado(false)}
    >
      <div className="crel-ventana">
        <div
          className="crel-pista"
          ref={pista}
          style={{
            // El % de translateX es sobre el ancho de la pista, que mide lo
            // mismo que la ventana. Dividido por las tarjetas que entran a
            // la vista da justo el ancho de una tarjeta.
            transform: `translateX(calc(${-indice} * (100% / ${porVista})))`,
          }}
        >
          {productos.map((p) => (
            <div className="crel-slot" key={p.producto_id}>
              <ProductCard data={p} onClick={() => onSelect?.(p)} />
            </div>
          ))}

          {onVerTodos ? (
            <div className="crel-slot" key="ver-todos">
              <button type="button" className="crel-todos" onClick={onVerTodos}>
                <span className="crel-todos-icono" aria-hidden="true">
                  →
                </span>
                <span className="crel-todos-titulo">See all parts</span>
                <span className="crel-todos-nota">
                  Browse the full catalog for this category
                </span>
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {total > porVista ? (
        <div className="crel-controles">
          <button
            type="button"
            className="crel-flecha"
            onClick={() => irA(indice - 1)}
            disabled={indice === 0}
            aria-label="Previous"
          >
            ‹
          </button>

          {/* Un punto por posicion, para saber donde se esta y poder ir
              directo sin esperar a que el carrusel llegue solo. */}
          <div className="crel-puntos">
            {Array.from({ length: ultimo + 1 }).map((_, i) => (
              <button
                type="button"
                key={i}
                className={"crel-punto" + (i === indice ? " crel-punto-on" : "")}
                onClick={() => irA(i)}
                aria-label={`Go to position ${i + 1}`}
                aria-current={i === indice}
              />
            ))}
          </div>

          <button
            type="button"
            className="crel-flecha"
            onClick={() => irA(indice + 1)}
            disabled={indice >= ultimo}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default CarruselRelacionados;
